import { api, ApiError } from '../lib/api';
import type { BuiltinAnswers, CustomAnswer, FrozenFile, FrozenSubmission } from '../../shared/types';
import type { StagedStatus } from '../../shared/stagedSubmission';
import { reportClientError } from '../lib/clientErrors';

/**
 * Staged delivery of a frozen submission (docs/pdf-browser-submission.md). Every step follows the
 * server's reported next actions; an unknown outcome stops here and is never resent. The attempt
 * ID travels only in request bodies.
 */
export type DeliveryPhase = { kind: 'starting' } | { kind: 'preparing' } | { kind: 'uploading'; index: number; total: number } | { kind: 'finishing' };
export type DeliveryOutcome =
  | { kind: 'complete' }
  /** The outcome is not known yet. `organizer` when only the organizer can resolve it. */
  | { kind: 'pending'; organizer: boolean; offline?: boolean }
  /** The event, its revision, Google or admission no longer allow this attempt to finish. */
  | { kind: 'blocked'; reason: 'closed' | 'revision_changed' | 'google_unavailable' | 'expired' | 'unavailable' | 'rejected' }
  /** A definite refusal with nothing left uncertain; the participant may try again. */
  | { kind: 'retry'; code: 'rate_limited' | 'upload_rejected' | 'sheet_busy' }
  /** A form still to send is not in memory and could not be rebuilt identically. */
  | { kind: 'missing'; file: FrozenFile; reason: 'changed' };

export interface FinalizeAnswers { builtin: BuiltinAnswers; custom: Record<string, CustomAnswer>; conductAcknowledgment: string }
export interface DeliveryInput {
  eventId: string; attemptId: string; snapshot: FrozenSubmission; answers: FinalizeAnswers;
  /** Request-only parts of Drive file names. */
  firstName: string; lastName: string; dateOfBirth: string;
  /** Check status before anything else: after a reload, timeout or earlier failure. */
  resume: boolean;
  /** The frozen bytes for a role, rebuilt if needed. */
  body: (file: FrozenFile) => Promise<{ body: Uint8Array } | { missing: 'changed' }>;
  onPhase?: (phase: DeliveryPhase) => void;
}

const base = (input: DeliveryInput) => ({ eventId: input.eventId, ...(input.snapshot.publishedRevisionId ? { publishedRevisionId: input.snapshot.publishedRevisionId } : {}), submissionAttemptId: input.attemptId, protocol: input.snapshot.protocol });
const manifest = (snapshot: FrozenSubmission) => snapshot.files.map(({ kind, documentId, bytes, mediaType }) => ({ kind, ...(documentId ? { documentId } : {}), bytes, mediaType }));

/** Map a refusal to an outcome. Network failures leave the outcome unknown. */
function refused(error: unknown): DeliveryOutcome {
  if (!(error instanceof ApiError)) { reportClientError('register: deliver', error); return { kind: 'pending', organizer: false, offline: true }; }
  switch (error.code) {
    case 'closed': case 'not_found': return { kind: 'blocked', reason: 'closed' };
    case 'revision_conflict': return { kind: 'blocked', reason: 'revision_changed' };
    case 'google_unavailable': case 'revision_unavailable': case 'unavailable': return { kind: 'blocked', reason: 'google_unavailable' };
    case 'submission_unavailable': case 'additional_forms_unavailable': return { kind: 'blocked', reason: 'unavailable' };
    case 'rate_limited': case 'upload_rejected': case 'sheet_busy': return { kind: 'retry', code: error.code };
    case 'upload_in_progress': case 'finalize_in_progress': return { kind: 'pending', organizer: false };
    // A server failure leaves the outcome unknown; a 4xx refused the frozen submission itself,
    // which cannot be changed under this attempt.
    default: return error.status >= 500 ? { kind: 'pending', organizer: false, offline: true } : { kind: 'blocked', reason: 'rejected' };
  }
}

/** What the status allows. null means an action (upload or finalize) is available. */
function settled(status: StagedStatus): DeliveryOutcome | null {
  if (status.state === 'complete') return { kind: 'complete' };
  if (status.state === 'expired') return { kind: 'blocked', reason: 'expired' };
  if (status.next.includes('upload') || status.next.includes('finalize')) return null;
  if (status.next.includes('check_later') || status.state === 'append_pending' || status.state === 'finalizing') return { kind: 'pending', organizer: status.next.includes('contact_organizer') };
  if (status.reason) return { kind: 'blocked', reason: status.reason };
  if (status.next.includes('refresh_event')) return { kind: 'blocked', reason: 'revision_changed' };
  return { kind: 'pending', organizer: true };
}

export async function deliver(input: DeliveryInput): Promise<DeliveryOutcome> {
  const status = () => api.post<StagedStatus>('/public/submission/status', { eventId: input.eventId, submissionAttemptId: input.attemptId });
  const start = () => { input.onPhase?.({ kind: 'starting' }); return api.post<StagedStatus>('/public/submission/start', { ...base(input), manifest: manifest(input.snapshot) }); };
  let current: StagedStatus;
  try {
    current = input.resume ? await status() : await start();
    // Only an attempt the server has never seen is started; start is idempotent for this key.
    if (current.state === 'not_found') current = await start();
  } catch (error) { return refused(error); }

  for (let round = 0; round < 4; round++) {
    const done = settled(current);
    if (done) return done;
    if (current.next.includes('upload')) {
      const states = new Map(current.files.map(f => [f.role, f.state]));
      const total = input.snapshot.files.length, delivered = input.snapshot.files.filter(f => states.get(f.role) === 'uploaded').length;
      const waiting = input.snapshot.files.filter(f => states.get(f.role) === 'waiting' || states.get(f.role) === 'retryable');
      for (const [i, file] of waiting.entries()) {
        const body = await input.body(file);
        if ('missing' in body) return { kind: 'missing', file, reason: body.missing };
        input.onPhase?.({ kind: 'uploading', index: delivered + i + 1, total });
        const form = new FormData();
        form.set('metadata', JSON.stringify({ ...base(input), role: file.role, bytes: file.bytes, firstName: input.firstName, lastName: input.lastName, dateOfBirth: input.dateOfBirth }));
        form.set('file', new File([body.body as BlobPart], 'form.pdf', { type: file.mediaType }), 'form.pdf');
        try {
          const result = await api.postForm<{ role: string; state: 'uploaded' | 'uploading' }>('/public/submission/file', form);
          // Google's outcome is unknown: never resend; status resolves it.
          if (result.state !== 'uploaded') return { kind: 'pending', organizer: false };
        } catch (error) {
          if (error instanceof ApiError && error.code === 'upload_unavailable') break;
          return refused(error);
        }
      }
    } else {
      input.onPhase?.({ kind: 'finishing' });
      try {
        const result = await api.post<StagedStatus>('/public/submission/finalize', { ...base(input), ...input.answers, signedOn: input.snapshot.signedOn, signedBy: input.snapshot.signedBy, documents: input.snapshot.documents });
        current = result;
        continue;
      } catch (error) {
        if (!(error instanceof ApiError && error.code === 'files_incomplete')) return refused(error);
      }
    }
    try { current = await status(); } catch (error) { return refused(error); }
  }
  return { kind: 'pending', organizer: false };
}
